import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useSession } from '@/hooks/useSession';
import type { RouteGuardPredicate } from './route-guards';

/**
 * Защита маршрута: предикат от state (и опционально от location) решает,
 * допустим ли текущий URL. Если нет — редирект на /welcome (`/`) с replace,
 * чтобы пользователь не мог вернуться по «Назад» в защищённый маршрут с
 * битым state.
 *
 * Зачем нужно (Phase 2.6 hotfix):
 *  - После reload URL остаётся прежним. Без защиты родитель попадает на
 *    /stage1 без data, на /error без context, на /result без ответов и т.п.
 *  - localStorage.clear() из консоли + reload — то же самое.
 *  - Result.tsx раньше делал navigate('/error') при пустом state — это
 *    создавало бесконечную ловушку. Теперь эта логика заменена на ProtectedRoute.
 *
 * Predicate-функции вынесены в `route-guards.ts` (требование react-refresh:
 * один файл — один компонент).
 */

interface ProtectedRouteProps {
  /** Возвращает true — рендерим children, false — редирект на `/`. */
  predicate: RouteGuardPredicate;
  children: ReactNode;
}

export function ProtectedRoute({ predicate, children }: ProtectedRouteProps) {
  const { state } = useSession();
  const location = useLocation();
  if (!predicate(state, location)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
